/**
 * Recovery for stale-deploy chunk failures.
 *
 * After a new deploy, browsers holding the previous HTML (or a cached router
 * state) request hashed chunks like /assets/ChatWidget-hVycvnza.js that no
 * longer exist. The dynamic import rejects, TanStack's error boundary catches
 * it, and the user sees "This page didn't load" on a random page. Recover by
 * clearing caches and reloading once per deploy generation.
 */

const GUARD_KEY = "chunk-recovery-attempt";
const GUARD_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /loading chunk \d+ failed/i,
  /loading css chunk/i,
  /unable to preload css/i,
  /'text\/html' is not a valid javascript mime type/i,
];

export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : "";
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function recentlyAttempted(): boolean {
  try {
    const raw = sessionStorage.getItem(GUARD_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < GUARD_WINDOW_MS;
  } catch {
    return false;
  }
}

function markAttempt() {
  try {
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {}
}

/**
 * Clears asset caches / service workers and reloads the current URL.
 * No-ops if we already tried within the guard window, so a genuinely broken
 * build can never turn into a reload loop.
 */
export async function recoverFromChunkError(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (recentlyAttempted()) return false;
  markAttempt();

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {}
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {}

  // A plain reload can be served the SAME stale HTML (which still references
  // deleted chunk filenames), leaving the user on a blank screen. Force a
  // fresh document fetch with a cache-buster.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_cb", Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return true;
}

let installed = false;

/** Catches chunk failures that never reach a React error boundary. */
export function installChunkErrorRecovery() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Vite fires this when a modulepreload/dynamic import fails.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    void recoverFromChunkError();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError((event as PromiseRejectionEvent).reason)) {
      void recoverFromChunkError();
    }
  });

  window.addEventListener("error", (event) => {
    const target = event.target as HTMLElement | null;
    if (target && target.tagName === "SCRIPT") {
      const src = (target as HTMLScriptElement).src || "";
      if (src.includes("/assets/")) void recoverFromChunkError();
      return;
    }
    if (isChunkLoadError((event as ErrorEvent).error ?? event.message)) {
      void recoverFromChunkError();
    }
  }, true);
}
